/**
 * Purchase Order API wrappers — Phase 65
 *
 * Uses the same `API` axios instance from adminApi.ts (MSAL-intercepted).
 * Token-based endpoints (approval email links) use a plain axios call
 * without authentication headers.
 */

import axios from 'axios';
import { API } from '../adminApi';   // MSAL-authenticated instance
import type {
  PurchaseOrder,
  POListResponse,
  CreatePORequest,
  UpdatePORequest,
  MatchInvoiceRequest,
  UnmatchInvoiceRequest,
  ApproveDeclinePORequest,
  POReviewToken,
  SubmitPOResponse,
  POStatus,
} from '../types/poTypes';

// ── List ─────────────────────────────────────────────────────────────────────

export interface POListParams {
  page?: number;
  page_size?: number;
  status?: POStatus;
  job_number?: string;
  search?: string;
}

export async function listPOs(params: POListParams = {}): Promise<POListResponse> {
  const res = await API.get<POListResponse>('/mgmt/po/list', { params });
  return res.data;
}

// ── Create ───────────────────────────────────────────────────────────────────

export interface CreatePOResponse {
  po_id: number;
  po_number: string;
  total_amount: number;
}

export async function createPO(payload: CreatePORequest): Promise<CreatePOResponse> {
  const res = await API.post<CreatePOResponse>('/mgmt/po/create', payload);
  return res.data;
}

// ── Get single PO ────────────────────────────────────────────────────────────

export async function getPO(poId: number): Promise<PurchaseOrder> {
  const res = await API.get<PurchaseOrder>(`/mgmt/po/${poId}`);
  return res.data;
}

// ── Update (DRAFT only) ──────────────────────────────────────────────────────

export interface UpdatePOResponse {
  po_id: number;
  total_amount: number;
}

export async function updatePO(poId: number, payload: UpdatePORequest): Promise<UpdatePOResponse> {
  const res = await API.put<UpdatePOResponse>(`/mgmt/po/${poId}`, payload);
  return res.data;
}

// ── Submit for approval ──────────────────────────────────────────────────────

export async function submitPO(poId: number): Promise<SubmitPOResponse> {
  const res = await API.post<SubmitPOResponse>(`/mgmt/po/${poId}/submit`);
  return res.data;
}

// ── Approve (first-tier, requires Entra session) ─────────────────────────────

export interface ApprovePOResponse {
  po_id: number;
  status: POStatus;
}

export async function approvePO(
  poId: number,
  payload: ApproveDeclinePORequest = {},
): Promise<ApprovePOResponse> {
  const res = await API.post<ApprovePOResponse>(`/mgmt/po/${poId}/approve`, payload);
  return res.data;
}

// ── Senior approve ───────────────────────────────────────────────────────────

export async function seniorApprovePO(poId: number): Promise<ApprovePOResponse> {
  const res = await API.post<ApprovePOResponse>(`/mgmt/po/${poId}/senior-approve`);
  return res.data;
}

// ── Decline ──────────────────────────────────────────────────────────────────

export async function declinePO(
  poId: number,
  payload: ApproveDeclinePORequest,
): Promise<ApprovePOResponse> {
  const res = await API.post<ApprovePOResponse>(`/mgmt/po/${poId}/decline`, payload);
  return res.data;
}

// ── Issue ────────────────────────────────────────────────────────────────────

export interface IssuePOResponse {
  po_id: number;
  status: POStatus;
  po_pdf_blob_url: string | null;
}

export async function issuePO(poId: number): Promise<IssuePOResponse> {
  const res = await API.post<IssuePOResponse>(`/mgmt/po/${poId}/issue`);
  return res.data;
}

// ── Close ────────────────────────────────────────────────────────────────────

export async function closePO(poId: number, note?: string): Promise<ApprovePOResponse> {
  const res = await API.post<ApprovePOResponse>(`/mgmt/po/${poId}/close`, { note });
  return res.data;
}

// ── PDF (SAS URL — ISSUED POs only) ──────────────────────────────────────────

export async function getPOPdfUrl(poId: number): Promise<string> {
  const res = await API.get<{ url: string }>(`/mgmt/po/${poId}/pdf`);
  return res.data.url;
}

// ── Print PDF on-the-fly (any status) ────────────────────────────────────────
// Calls GET /mgmt/po/{po_id}/print which generates the PDF without changing
// PO status or saving to blob storage.  Returns a blob object URL suitable
// for window.open() — caller is responsible for revoking it.

export async function printPO(poId: number): Promise<string> {
  const res = await API.get(`/mgmt/po/${poId}/print`, { responseType: 'blob' });
  const blob = new Blob([res.data], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

// ── Invoice matching ─────────────────────────────────────────────────────────

export interface MatchInvoiceResponse {
  match_id: number;
  po_id: number;
}

export async function matchInvoiceToPO(
  poId: number,
  payload: MatchInvoiceRequest,
): Promise<MatchInvoiceResponse> {
  const res = await API.post<MatchInvoiceResponse>(`/mgmt/po/${poId}/match-invoice`, payload);
  return res.data;
}

export interface UnmatchInvoiceResponse {
  po_id: number;
  match_id: number;
  unmatched: boolean;
}

export async function unmatchInvoiceFromPO(
  poId: number,
  payload: UnmatchInvoiceRequest,
): Promise<UnmatchInvoiceResponse> {
  const res = await API.post<UnmatchInvoiceResponse>(`/mgmt/po/${poId}/unmatch-invoice`, payload);
  return res.data;
}

// ── Token-based review (no Entra session — email link) ───────────────────────

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
const unauthenticatedClient = axios.create({ baseURL: BASE_URL });

export async function getPOByToken(token: string): Promise<PurchaseOrder> {
  const res = await unauthenticatedClient.get<PurchaseOrder>('/mgmt/po/review', {
    params: { token },
  });
  return res.data;
}

export interface TokenActionResponse {
  po_id: number;
  status: POStatus;
}

export async function submitTokenAction(payload: POReviewToken): Promise<TokenActionResponse> {
  const res = await unauthenticatedClient.post<TokenActionResponse>(
    '/mgmt/po/review/action',
    payload,
  );
  return res.data;
}
